import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportFormat } from '../../application/metrics';
import { Button, HelpTooltip, LoadingSpinner } from '../components/common';
import { AccessDenied } from '../components/common/AccessDenied';
import { ExportButtons } from '../components/metrics/ExportButtons';
import { firstVisibleHousePath } from '../../application/navigation/menuHouses';
import { createPanelCashHttpClient } from '../../infrastructure/http/panelCashHttpClient';
import { listTenants, type TenantGridDTO } from '../../infrastructure/http/tenantsApi';
import {
  createOwnership,
  ensureOwnershipDefaults,
  getOwnershipPreview,
  downloadOwnershipReport,
  listOwnership,
  setOwnershipStatus,
  updateOwnership,
  type OwnershipParticipationDTO,
  type OwnershipParticipationPayload,
  type OwnershipPreviewDTO,
} from '../../infrastructure/http/ownershipApi';
import { useAuth } from '../contexts/useAuth';
import { usePermissions } from '../contexts/permissionsContextCore';

type ParticipantType = OwnershipParticipationPayload['participantType'];
type PeriodMonths = 1 | 3 | 6 | 12;

interface OfficeOption {
  id: string;
  label: string;
  address?: string;
}

interface FormState {
  tenantId: string;
  consultorioId: string;
  participantName: string;
  participantEmail: string;
  participantType: ParticipantType;
  percentage: string;
  validFrom: string;
  validTo: string;
  active: boolean;
}

const today = new Date();
const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
const firstDayOfMonth = `${currentMonth}-01`;
const periodMonthOptions: PeriodMonths[] = [1, 3, 6, 12];

const emptyForm = (tenantId: string, consultorioId: string): FormState => ({
  tenantId,
  consultorioId,
  participantName: '',
  participantEmail: '',
  participantType: 'owner',
  percentage: '',
  validFrom: firstDayOfMonth,
  validTo: '',
  active: true,
});

const currency = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return currency.format(value || 0);
}

function formatPercent(value: number): string {
  return `${Number(value || 0).toLocaleString('es-AR', { maximumFractionDigits: 2 })}%`;
}

function formatDate(value: string, locale: string): string {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(value: string, locale: string): string {
  if (!value) return '';
  return new Date(value).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorCode(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'request_failed';
}

function mapOfficeOptions(items: Record<string, unknown>[]): OfficeOption[] {
  return items
    .map((item) => {
      const id = item.id ?? item.Id;
      const label = item.nombre ?? item.Nombre ?? item.name ?? item.Name;
      const address = item.direccion ?? item.Direccion ?? item.address ?? item.Address;
      if (id == null || label == null) return null;
      return {
        id: String(id),
        label: String(label),
        address: address == null ? undefined : String(address),
      };
    })
    .filter((item): item is OfficeOption => item !== null);
}

function toPayload(form: FormState): OwnershipParticipationPayload {
  return {
    tenantId: form.tenantId,
    consultorioId: form.consultorioId ? Number(form.consultorioId) : null,
    participantName: form.participantName.trim(),
    participantEmail: form.participantEmail.trim() || null,
    participantType: form.participantType,
    percentage: Number(form.percentage),
    validFrom: form.validFrom,
    validTo: form.validTo || null,
    active: form.active,
  };
}

function toForm(row: OwnershipParticipationDTO): FormState {
  return {
    tenantId: row.tenantId,
    consultorioId: row.consultorioId == null ? '' : String(row.consultorioId),
    participantName: row.participantName,
    participantEmail: row.participantEmail ?? '',
    participantType: row.participantType,
    percentage: String(row.percentage),
    validFrom: row.validFrom,
    validTo: row.validTo ?? '',
    active: row.active,
  };
}

export const OwnershipParticipationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const { hasPermission, isOwner, isSimulating } = usePermissions();
  const canView = isOwner && !isSimulating;
  const fallbackPath = firstVisibleHousePath(hasPermission) ?? '/';

  const allowedTenantIdsKey = user?.scope.allowedTenantIds.join('|') ?? '';
  const tenantIds = useMemo(
    () => allowedTenantIdsKey.split('|').map((id) => id.trim()).filter(Boolean),
    [allowedTenantIdsKey]
  );

  const [tenants, setTenants] = useState<TenantGridDTO[]>([]);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState(() => tenantIds[0] ?? '');
  const [selectedConsultorioId, setSelectedConsultorioId] = useState('');
  const [period, setPeriod] = useState(currentMonth);
  const [periodMonths, setPeriodMonths] = useState<PeriodMonths>(1);
  const [rows, setRows] = useState<OwnershipParticipationDTO[]>([]);
  const [preview, setPreview] = useState<OwnershipPreviewDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDialog, setErrorDialog] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingRow, setEditingRow] = useState<OwnershipParticipationDTO | null>(null);
  const [form, setForm] = useState<FormState>(() => emptyForm(selectedTenantId, selectedConsultorioId));
  const [confirmStatus, setConfirmStatus] = useState<OwnershipParticipationDTO | null>(null);
  const [participationTab, setParticipationTab] = useState<'distribution' | 'configuration'>('distribution');
  const [detailsTab, setDetailsTab] = useState<'income' | 'commissions'>('income');
  const [participationOpen, setParticipationOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const selectedTenant = tenants.find((tenant) => tenant.id === selectedTenantId);
  const selectedOffice = offices.find((office) => office.id === selectedConsultorioId);
  const officeLabelById = useMemo(() => new Map(offices.map((office) => [office.id, office.label])), [offices]);

  const loadTenants = useCallback(async () => {
    if (!token || !canView) return;
    const response = await listTenants(token);
    const allowed = tenantIds.length > 0 ? response.filter((tenant) => tenantIds.includes(tenant.id)) : response;
    setTenants(allowed);
    if (!selectedTenantId && allowed[0]) setSelectedTenantId(allowed[0].id);
  }, [canView, selectedTenantId, tenantIds, token]);

  const loadOffices = useCallback(async () => {
    if (!token || !selectedTenantId) {
      setOffices([]);
      return;
    }
    const client = createPanelCashHttpClient(token, selectedTenantId);
    const response = await client.get<Record<string, unknown>[]>('/offices');
    setOffices(mapOfficeOptions(response));
  }, [selectedTenantId, token]);

  const loadData = useCallback(async () => {
    if (!token || !selectedTenantId || !period) return;
    setLoading(true);
    setError(null);
    try {
      const [year, month] = period.split('-').map(Number);
      await ensureOwnershipDefaults(token, selectedTenantId);
      const [list, nextPreview] = await Promise.all([
        listOwnership(token, { tenantId: selectedTenantId }),
        getOwnershipPreview(token, {
          tenantId: selectedTenantId,
          year,
          month,
          months: periodMonths,
          consultorioId: selectedConsultorioId ? Number(selectedConsultorioId) : null,
        }),
      ]);
      setRows(list);
      setPreview(nextPreview);
    } catch (err) {
      const code = errorCode(err);
      setError(code);
      setErrorDialog(code);
    } finally {
      setLoading(false);
    }
  }, [period, periodMonths, selectedConsultorioId, selectedTenantId, token]);

  useEffect(() => { void loadTenants(); }, [loadTenants]);
  useEffect(() => { void loadOffices(); }, [loadOffices]);
  useEffect(() => { void loadData(); }, [loadData]);

  const selectedTenantRows = useMemo(
    () => rows.filter((row) => row.tenantId === selectedTenantId),
    [rows, selectedTenantId]
  );
  const selectedOfficeRows = useMemo(
    () => selectedConsultorioId ? selectedTenantRows.filter((row) => row.consultorioId === Number(selectedConsultorioId)) : [],
    [selectedConsultorioId, selectedTenantRows]
  );
  const globalRows = useMemo(
    () => selectedTenantRows.filter((row) => row.consultorioId == null),
    [selectedTenantRows]
  );
  const usingInheritedConfiguration = Boolean(selectedConsultorioId && selectedOfficeRows.length === 0 && globalRows.length > 0);
  const filteredRows = useMemo(() => {
    if (!selectedConsultorioId) return selectedTenantRows;
    return selectedOfficeRows.length > 0 ? selectedOfficeRows : globalRows;
  }, [globalRows, selectedConsultorioId, selectedOfficeRows, selectedTenantRows]);

  const scopeLabel = useCallback((row: OwnershipParticipationDTO) => {
    if (selectedConsultorioId && row.consultorioId == null) return t('pages.ownership.inheritedAllOffices');
    if (selectedConsultorioId) return null;
    if (row.consultorioId == null) return t('pages.ownership.allOffices');
    return officeLabelById.get(String(row.consultorioId)) ?? `#${row.consultorioId}`;
  }, [officeLabelById, selectedConsultorioId, t]);

  const periodRangeLabel = preview
    ? `${formatDate(preview.periodStart, i18n.language)} - ${formatDate(preview.periodEnd, i18n.language)}`
    : '';
  const configuredPercentage = preview?.configuredPercentage ?? 0;
  const distributionStatus = (() => {
    if (!preview || configuredPercentage <= 0.004) {
      return { key: 'empty', className: 'text-gray-500 dark:text-gray-400', values: {} };
    }
    if (configuredPercentage > 100.004) {
      return {
        key: 'over',
        className: 'text-red-600 dark:text-red-300',
        values: { percent: formatPercent(configuredPercentage - 100) },
      };
    }
    if (configuredPercentage >= 99.995) {
      return { key: 'complete', className: 'text-teal-700 dark:text-teal-300', values: { percent: formatPercent(configuredPercentage) } };
    }
    return {
      key: 'missing',
      className: 'text-amber-700 dark:text-amber-300',
      values: {
        assigned: formatPercent(configuredPercentage),
        missing: formatPercent(100 - configuredPercentage),
      },
    };
  })();
  const confirmingInheritedStatus = Boolean(confirmStatus && selectedConsultorioId && confirmStatus.consultorioId == null);
  const confirmStatusMessageKey = confirmStatus?.active
    ? (confirmingInheritedStatus ? 'disableInheritedMessage' : 'disableMessage')
    : (confirmingInheritedStatus ? 'enableInheritedMessage' : 'enableMessage');

  const openCreate = () => {
    setEditingRow(null);
    setForm(emptyForm(selectedTenantId, selectedConsultorioId));
    setModalMode('create');
  };

  const openEdit = (row: OwnershipParticipationDTO) => {
    setEditingRow(row);
    setForm(toForm(row));
    setModalMode('edit');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(form);
      if (modalMode === 'edit' && editingRow) {
        await updateOwnership(token, editingRow.id, payload);
      } else {
        await createOwnership(token, payload);
      }
      setModalMode(null);
      setEditingRow(null);
      await loadData();
    } catch (err) {
      const code = errorCode(err);
      setError(code);
      setErrorDialog(code);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusConfirm = async () => {
    if (!token || !confirmStatus) return;
    setSaving(true);
    setError(null);
    try {
      await setOwnershipStatus(token, confirmStatus.id, !confirmStatus.active);
      setConfirmStatus(null);
      await loadData();
    } catch (err) {
      const code = errorCode(err);
      setError(code);
      setErrorDialog(code);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format: ReportFormat) => {
    if (!token || !selectedTenantId || !period) return;
    const [year, month] = period.split('-').map(Number);
    await downloadOwnershipReport(token, {
      tenantId: selectedTenantId,
      year,
      month,
      months: periodMonths,
      consultorioId: selectedConsultorioId ? Number(selectedConsultorioId) : null,
    }, format);
  };

  if (!canView) {
    return <AccessDenied title={t('pages.ownership.forbiddenTitle')} message={t('pages.ownership.forbiddenMessage')} homeTo={fallbackPath} />;
  }

  return (
    <div className="panel-page space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('pages.ownership.title')}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{t('pages.ownership.description')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <ExportButtons onExport={handleExport} disabled={loading || !selectedTenantId || !preview} compact />
          <Button size="sm" onClick={openCreate} disabled={!selectedTenantId}>{t('pages.ownership.newParticipant')}</Button>
        </div>
      </header>

      <section className="rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="grid gap-2 xl:grid-cols-[1.25fr_0.9fr_1.15fr_auto] xl:items-end">
          <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
            {t('pages.ownership.clinic')}
            <select
              value={selectedTenantId}
              onChange={(event) => { setSelectedTenantId(event.target.value); setSelectedConsultorioId(''); }}
              className="mt-1 h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.nombre}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
            {t('pages.ownership.office')}
            <select
              value={selectedConsultorioId}
              onChange={(event) => setSelectedConsultorioId(event.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('pages.ownership.allOffices')}</option>
              {offices.map((office) => <option key={office.id} value={office.id}>{office.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
            {t('pages.ownership.period')}
            <div className="mt-1 grid gap-2 sm:grid-cols-[1fr_auto]">
              <input
                type="month"
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <div className="inline-flex rounded-md border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-900">
                {periodMonthOptions.map((months) => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => setPeriodMonths(months)}
                    className={`h-8 min-w-11 rounded px-2.5 text-xs font-semibold transition ${
                      periodMonths === months
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t(`pages.ownership.periodOptions.${months}`)}
                  </button>
                ))}
              </div>
            </div>
          </label>
          <Button variant="secondary" onClick={() => void loadData()} disabled={loading || !selectedTenantId}>
            {loading ? <LoadingSpinner size="sm" /> : t('common.update')}
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          {selectedOffice?.address && <span className="rounded-full border border-gray-200 px-2.5 py-0.5 dark:border-gray-700">{selectedOffice.address}</span>}
          {periodRangeLabel && <span className="rounded-full border border-gray-200 px-2.5 py-0.5 dark:border-gray-700">{periodRangeLabel}</span>}
        </div>
      </section>

      {error && <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">{t(`pages.ownership.errors.${error}`, { defaultValue: error })}</div>}

      <section className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-5">
        {[
          ['totalCollected', preview?.totalCollected ?? 0, 'text-emerald-300'],
          ['professionalCommission', preview?.professionalCommission ?? 0, 'text-amber-300'],
          ['clinicNet', preview?.clinicNet ?? 0, 'text-cyan-300'],
          ['distributed', preview?.distributed ?? 0, 'text-teal-300'],
          ['undistributed', preview?.undistributed ?? 0, 'text-red-300'],
        ].map(([key, value, color]) => (
          <article key={String(key)} className="relative overflow-hidden rounded-lg border border-gray-200 bg-white px-3 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <span className={`absolute inset-x-0 top-0 h-1 ${color === 'text-red-300' ? 'bg-red-500' : color === 'text-amber-300' ? 'bg-amber-500' : 'bg-teal-500'}`} />
            <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t(`pages.ownership.cards.${key}`)}
              <HelpTooltip content={t(`pages.ownership.tooltips.${key}`)} variant="info" size="sm" />
            </p>
            <p className={`mt-1.5 text-xl font-bold leading-tight ${color}`}>{formatCurrency(Number(value))}</p>
          </article>
        ))}
      </section>

      <section className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className={`flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between ${participationOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            <div>
              <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                {participationTab === 'distribution' ? t('pages.ownership.distribution') : t('pages.ownership.configuration')}
                {participationTab === 'distribution' && <HelpTooltip content={t('pages.ownership.tooltips.distribution')} variant="info" />}
              </h2>
              {participationTab === 'distribution' ? (
                <p className={`text-sm font-semibold ${distributionStatus.className}`}>
                  {t(`pages.ownership.distributionStatus.${distributionStatus.key}`, distributionStatus.values)}
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTenant?.nombre ?? '-'}</p>
                  {usingInheritedConfiguration && (
                    <p className="mt-1 text-xs font-semibold text-cyan-700 dark:text-cyan-300">{t('pages.ownership.inheritedConfiguration')}</p>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {participationOpen && (
                <div className="inline-flex w-fit rounded-md border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={() => setParticipationTab('distribution')}
                    className={`h-8 rounded px-3 text-xs font-semibold transition ${
                      participationTab === 'distribution'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('pages.ownership.distribution')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setParticipationTab('configuration')}
                    className={`h-8 rounded px-3 text-xs font-semibold transition ${
                      participationTab === 'configuration'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('pages.ownership.configuration')}
                  </button>
                </div>
              )}
              <button
                type="button"
                aria-expanded={participationOpen}
                onClick={() => setParticipationOpen((value) => !value)}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {participationOpen ? t('pages.ownership.actions.hideSection') : t('pages.ownership.actions.showSection')}
                <span aria-hidden="true" className="text-xs">{participationOpen ? '⌃' : '⌄'}</span>
              </button>
            </div>
          </div>
          {participationOpen && (participationTab === 'distribution' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.table.name')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.table.type')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.table.percentage')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.table.amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(preview?.participants ?? []).map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{item.participantName}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{t(`pages.ownership.types.${item.participantType}`)}</td>
                      <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                        <div className="ml-auto w-36">
                          <div className="mb-1 text-right font-semibold">{formatPercent(item.percentage)}</div>
                          <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
                            <div className="h-1.5 rounded-full bg-teal-400" style={{ width: `${Math.min(Math.max(item.percentage, 0), 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-teal-600 dark:text-teal-300">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  {(!preview || preview.participants.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">{t('pages.ownership.emptyDistribution')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="max-h-[520px] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.table.name')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.table.percentage')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.table.status')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 dark:text-white">{row.participantName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{row.participantEmail || t(`pages.ownership.types.${row.participantType}`)}</p>
                        {scopeLabel(row) && <p className="mt-1 text-xs text-cyan-700 dark:text-cyan-300">{scopeLabel(row)}</p>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{formatPercent(row.percentage)}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${row.active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{row.active ? t('pages.ownership.status.active') : t('pages.ownership.status.inactive')}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEdit(row)}>{t('common.edit')}</Button>
                          <Button size="sm" variant={row.active ? 'danger' : 'secondary'} onClick={() => setConfirmStatus(row)}>
                            {row.active ? t('pages.ownership.actions.disable') : t('pages.ownership.actions.enable')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">{t('pages.ownership.emptyConfiguration')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className={`flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between ${detailsOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            <div>
              <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                {t('pages.ownership.details.title')}
                <HelpTooltip
                  content={t(detailsTab === 'income' ? 'pages.ownership.tooltips.incomeDetails' : 'pages.ownership.tooltips.commissionDetails')}
                  variant="info"
                />
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {detailsTab === 'income'
                  ? t('pages.ownership.details.records', { count: preview?.incomeDetails.length ?? 0 })
                  : t('pages.ownership.details.records', { count: preview?.professionalCommissionDetails.length ?? 0 })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {detailsOpen && (
                <div className="inline-flex w-fit rounded-md border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={() => setDetailsTab('income')}
                    className={`h-8 rounded px-3 text-xs font-semibold transition ${
                      detailsTab === 'income'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('pages.ownership.details.incomeTitle')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDetailsTab('commissions')}
                    className={`h-8 rounded px-3 text-xs font-semibold transition ${
                      detailsTab === 'commissions'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('pages.ownership.details.commissionTitle')}
                  </button>
                </div>
              )}
              <button
                type="button"
                aria-expanded={detailsOpen}
                onClick={() => setDetailsOpen((value) => !value)}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {detailsOpen ? t('pages.ownership.actions.hideSection') : t('pages.ownership.actions.showSection')}
                <span aria-hidden="true" className="text-xs">{detailsOpen ? '⌃' : '⌄'}</span>
              </button>
            </div>
          </div>

          {detailsOpen && (detailsTab === 'income' ? (
            <div className="max-h-96 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.details.date')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.office')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.details.patient')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.details.professional')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.details.paymentMethod')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.table.amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(preview?.incomeDetails ?? []).map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDateTime(item.date, i18n.language)}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.consultorioName ?? '-'}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 dark:text-white">{item.patientName ?? '-'}</p>
                        {item.patientDocument && <p className="text-xs text-gray-500 dark:text-gray-400">{item.patientDocument}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.professionalName ?? '-'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.paymentMethod ?? '-'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-300">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  {(!preview || preview.incomeDetails.length === 0) && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">{t('pages.ownership.details.emptyIncome')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="max-h-96 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.details.professional')}</th>
                    <th className="px-4 py-3 text-left">{t('pages.ownership.office')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.details.movements')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.details.grossIncome')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.details.commissionPercentage')}</th>
                    <th className="px-4 py-3 text-right">{t('pages.ownership.details.commissionAmount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(preview?.professionalCommissionDetails ?? []).map((item) => (
                    <tr key={`${item.professionalId ?? 'none'}-${item.consultorioId ?? 'all'}-${item.commissionPercentage}`}>
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{item.professionalName ?? t('pages.ownership.details.noProfessional')}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.consultorioName ?? '-'}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{item.movements}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(item.grossIncome)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{formatPercent(item.commissionPercentage)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-amber-600 dark:text-amber-300">{formatCurrency(item.commissionAmount)}</td>
                    </tr>
                  ))}
                  {(!preview || preview.professionalCommissionDetails.length === 0) && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">{t('pages.ownership.details.emptyCommissions')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </section>

      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={() => !saving && setModalMode(null)}>
          <form className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-800" onSubmit={handleSubmit} onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{modalMode === 'edit' ? t('pages.ownership.editParticipant') : t('pages.ownership.newParticipant')}</h2>
              <button type="button" className="text-2xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={() => !saving && setModalMode(null)}>×</button>
            </div>
            <div className="grid max-h-[70vh] gap-4 overflow-y-auto p-6 md:grid-cols-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 md:col-span-2">
                {t('pages.ownership.form.name')}
                <input required value={form.participantName} onChange={(e) => setForm({ ...form, participantName: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.email')}<HelpTooltip content={t('pages.ownership.tooltips.email')} variant="info" size="sm" /></span>
                <input required type="email" value={form.participantEmail} onChange={(e) => setForm({ ...form, participantEmail: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.type')}<HelpTooltip content={t('pages.ownership.tooltips.type')} variant="info" size="sm" /></span>
                <select value={form.participantType} onChange={(e) => setForm({ ...form, participantType: e.target.value as ParticipantType })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  {(['owner', 'partner', 'admin', 'other'] as ParticipantType[]).map((type) => <option key={type} value={type}>{t(`pages.ownership.types.${type}`)}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.clinic')}<HelpTooltip content={t('pages.ownership.tooltips.scope')} variant="info" size="sm" /></span>
                <select required value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value, consultorioId: '' })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.nombre}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.office')}<HelpTooltip content={t('pages.ownership.tooltips.office')} variant="info" size="sm" /></span>
                <select value={form.consultorioId} onChange={(e) => setForm({ ...form, consultorioId: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  <option value="">{t('pages.ownership.allOffices')}</option>
                  {offices.map((office) => <option key={office.id} value={office.id}>{office.label}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.percentage')}<HelpTooltip content={t('pages.ownership.tooltips.percentage')} variant="info" size="sm" /></span>
                <input required min="0.01" max="100" step="0.01" type="number" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.validFrom')}<HelpTooltip content={t('pages.ownership.tooltips.validFrom')} variant="info" size="sm" /></span>
                <input required type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </label>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                <span className="inline-flex items-center gap-1">{t('pages.ownership.form.validTo')}<HelpTooltip content={t('pages.ownership.tooltips.validTo')} variant="info" size="sm" /></span>
                <input type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                {t('pages.ownership.status.active')}
              </label>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={() => setModalMode(null)} disabled={saving}>{t('common.cancel')}</Button>
              <Button type="submit" isLoading={saving}>{t('common.save')}</Button>
            </div>
          </form>
        </div>
      )}


      {errorDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={() => setErrorDialog(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-800" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('pages.ownership.errorTitle')}</h2>
            </div>
            <p className="px-6 py-5 text-sm text-gray-700 dark:text-gray-200">
              {t(`pages.ownership.errors.${errorDialog}`, { defaultValue: errorDialog })}
            </p>
            <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setErrorDialog(null)}>{t('common.close')}</Button>
            </div>
          </div>
        </div>
      )}

      {confirmStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={() => !saving && setConfirmStatus(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-800" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{confirmStatus.active ? t('pages.ownership.disableTitle') : t('pages.ownership.enableTitle')}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{confirmStatus.participantName} · {formatPercent(confirmStatus.percentage)}</p>
            </div>
            <p className="px-6 py-5 text-sm text-gray-700 dark:text-gray-200">{t(`pages.ownership.${confirmStatusMessageKey}`)}</p>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setConfirmStatus(null)} disabled={saving}>{t('common.cancel')}</Button>
              <Button variant={confirmStatus.active ? 'danger' : 'primary'} onClick={handleStatusConfirm} isLoading={saving}>{confirmStatus.active ? t('pages.ownership.actions.disable') : t('pages.ownership.actions.enable')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

